import axios from "axios";
import Cookies from "js-cookie";
import store from "@/store/index";
import router from "@/router/index";

import { EventBus } from "@/main.js";

const standard = axios.create({
  baseURL: process.env.VUE_APP_API_HOST,
  timeout: 100000,
  transformResponse: [
    function (data) {
      return data;
    },
  ],
});

const ACCESS_COOKIE = "access_token";
const REFRESH_COOKIE = "refresh_token";

// Uma renovação por vez: se várias requests tomarem 401 juntas, todas esperam
// a mesma promise. Disparar refreshes concorrentes quebraria a sessão, porque
// o Passport rotaciona o refresh_token a cada uso e invalida o anterior.
let refreshInFlight = null;

export function persistTokens(data) {
  const access_token = "Bearer " + data.access_token;

  Cookies.set(ACCESS_COOKIE, access_token, { expires: 365 });

  if (data.refresh_token) {
    Cookies.set(REFRESH_COOKIE, data.refresh_token, {
      expires: new Date(Date.now() + 8 * 60 * 60 * 1000),
    });
  }

  store.commit("authSuccess", access_token);
  standard.defaults.headers.common["Authorization"] = access_token;

  return access_token;
}

function requestNewToken() {
  const refresh_token = Cookies.get(REFRESH_COOKIE);

  if (!refresh_token) return Promise.resolve(null);

  // axios cru de propósito: passar pelo `standard` faria o 401 desta própria
  // chamada cair neste interceptor e recursar.
  return axios
    .post(process.env.VUE_APP_API_HOST + "/oauth/token", {
      grant_type: "refresh_token",
      refresh_token: refresh_token,
      client_id: process.env.VUE_APP_OAUTH_CLIENT_ID,
      client_secret: process.env.VUE_APP_OAUTH_CLIENT_SECRET,
      scope: "*",
    })
    .then(function (resp) {
      const data =
        typeof resp.data === "string" ? JSON.parse(resp.data) : resp.data;

      return persistTokens(data);
    })
    .catch(function () {
      // Refresh expirado ou já usado: some com ele para não ficar tentando.
      Cookies.remove(REFRESH_COOKIE);
      return null;
    });
}

function refreshAccessToken() {
  if (!refreshInFlight) {
    refreshInFlight = requestNewToken().finally(function () {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
}

standard.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (err) {
    const config = err.config || {};


    // Cancelamentos via CancelToken são intencionais — não exibir toast (VSUS-991)
    if (axios.isCancel(err)) return Promise.reject(err);

    // O access token agora dura 30min. Em 401, tenta renovar pelo refresh_token
    // e repetir a request uma única vez — é isso que faz a sessão expirar por
    // inatividade em vez de cair no meio do uso. Sessões de impersonation não
    // têm refresh_token, então caem direto no tratamento de 401 abaixo.
    if (
      err.response &&
      err.response.status === 401 &&
      !config.__isRetryAfterRefresh &&
      Cookies.get(REFRESH_COOKIE)
    ) {
      config.__isRetryAfterRefresh = true;

      // Se outra request já renovou enquanto esta estava no ar, o token atual
      // difere do que ela usou: aproveita em vez de gastar outro refresh. O
      // Passport rotaciona o refresh_token a cada uso, então um segundo
      // refresh invalidaria o que acabou de ser emitido e derrubaria a sessão.
      const usedToken = config.headers && config.headers.Authorization;
      const currentToken = Cookies.get(ACCESS_COOKIE);

      const renewed =
        currentToken && currentToken !== usedToken
          ? currentToken
          : await refreshAccessToken();

      if (renewed) {
        config.headers = config.headers || {};
        config.headers.Authorization = renewed;

        return standard(config);
      }
    }


    if (!config.suppressErrorNotify) {
      if (err.response) {
        switch (err.response.status) {
          case 422: {
            if (err.response.request.responseType == "blob") {
              EventBus.$emit(
                "errorNotify",
                "Erro ao solicitar o download deste arquivo",
              );
            } else {
              let data = err.response.data;
              if (typeof data == "string") {
                data = JSON.parse(data);
              }

              let errorShown = false;

              if (data.errors && typeof data.errors === "object") {
                for (let campo in data.errors) {
                  if (
                    Array.isArray(data.errors[campo]) &&
                    data.errors[campo].length > 0
                  ) {
                    EventBus.$emit("errorNotify", {
                      message: data.errors[campo][0],
                      title: data.title || null,
                      originalData: data,
                    });
                    errorShown = true;
                    break;
                  }
                }
              }

              if (!errorShown) {
                for (let campo in data) {
                  if (
                    campo !== "message" &&
                    campo !== "title" &&
                    Array.isArray(data[campo])
                  ) {
                    if (data[campo].length > 0) {
                      EventBus.$emit("errorNotify", {
                        message: data[campo][0],
                        title: data.title || null,
                        originalData: data,
                      });
                      errorShown = true;
                      break;
                    }
                  }
                }
              }

              if (!errorShown && data.message) {
                EventBus.$emit("errorNotify", {
                  message: data.message,
                  title: data.title || null,
                  originalData: data,
                });
                errorShown = true;
              }

              if (!errorShown) {
                EventBus.$emit("errorNotify", "Erro de validação");
              }
            }
            break;
          }
          case 400: {
            EventBus.$emit(
              "errorNotify",
              "400 - Algum parâmetro obrigatório não foi enviado ou é inválido.",
            );
            break;
          }
          case 401: {
            // Chegou aqui é porque não havia refresh ou ele já não valia mais.
            Cookies.remove(REFRESH_COOKIE);

            if (store.getters.isAuthenticated) {
              store.dispatch("logoutRequest").catch(function (err) {});
            }
            router.push("/");
            EventBus.$emit(
              "errorNotify",
              "401 - Usuário não autorizado. Refaça o login e tente novamente",
            );
            break;
          }
          case 404: {
            EventBus.$emit(
              "errorNotify",
              "404 - A URL configurada na requisição não existe.",
            );
            break;
          }
          case 405: {
            EventBus.$emit("errorNotify", "405 - Método não permitido.");
            break;
          }
          case 408: {
            EventBus.$emit(
              "errorNotify",
              "408 - Aplicação não retornou uma resposta dentro do tempo esperado.",
            );
            break;
          }
          case 500: {
            EventBus.$emit(
              "errorNotify",
              "500 - A requisição foi enviada, porém ele retornou algum erro interno",
            );
            break;
          }
          default: {
            EventBus.$emit("errorNotify", "Erro ao executar a requisição");
            break;
          }
        }
      } else {
        EventBus.$emit("errorNotify", "Erro ao executar a requisição");
      }
    }

    return Promise.reject(err);
  },
);

if (Cookies.get("access_token")) {
  standard.defaults.headers.common["Authorization"] =
    Cookies.get("access_token");
}
standard.defaults.headers.common["Access-Control-Allow-Origin"] = "*";

export default standard;
